import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { profileAPI } from "../services/api";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
import { toast } from "../hooks/use-toast";
import {
  User,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  IndianRupee,
  Upload,
  Download,
  Loader2,
  Clock,
  Edit,
} from "lucide-react";

const ViewProfile = () => {
  const { email } = useParams();
  const navigate = useNavigate();

  const [profile, setProfile] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, [email]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const res = await profileAPI.get(email);
      console.log("PROFILE:", res.data); // ✅ DEBUG
      setProfile(res.data || {});
    } catch {
      toast({ title: "Error loading profile", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

// ================= EXPERIENCE HELPER =================
const getexperience = () => {
  const years = Number(profile?.experienceYears) || 0;
  const months = Number(profile?.experienceMonths) || 0;

  if (!years && !months) {
    return <span className="text-gray-400">Not specified</span>;
  }

  const parts = [];
  if (years > 0) parts.push(`${years} ${years === 1 ? "year" : "years"}`);
  if (months > 0) parts.push(`${months} ${months === 1 ? "month" : "months"}`);

  return <span className="font-medium text-gray-800">{parts.join(" ")}</span>;
};

  // ✅ EXPERIENCE CALCULATION
  const getExperience = () => {
    if (profile.experience) return profile.experience;

    if (!profile.workExperience?.length) return "0 Years";

    let months = 0;

    profile.workExperience.forEach((exp) => {
      const start = new Date(exp.startDate);
      const end = exp.current ? new Date() : new Date(exp.endDate);
      months += (end - start) / (1000 * 60 * 60 * 24 * 30);
    });

    const years = Math.floor(months / 12);
    const rem = Math.floor(months % 12);

    return `${years}y ${rem}m`;
  };

  // ✅ RESUME UPLOAD
  const handleResumeUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      await profileAPI.uploadResume(email, file);
      toast({ title: "Resume uploaded" });
      fetchProfile();
    } catch {
      toast({ title: "Upload failed", variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <Loader2 className="animate-spin w-10 h-10 text-blue-500" />
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <Header />

      <div className="max-w-6xl mx-auto p-6">

        {/* HEADER */}
        <Card className="p-6 flex justify-between items-center mb-6">

          <div className="flex gap-4">

            {/* PHOTO */}
            {profile.photoUrl ? (
              <img
                src={`${BACKEND_URL}${profile.photoUrl}`}
                className="w-20 h-20 rounded-full object-cover"
              />
            ) : (
              <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center">
                <User />
              </div>
            )}

            <div>
              <h2 className="text-xl font-bold">
                {profile.fullName || "No Name"}
              </h2>

              <p className="text-gray-500">
                {profile.currentRole || "No Designation"}
              </p>

              <div className="flex gap-4 text-sm mt-2">

                <span className="flex items-center gap-1">
                  <Mail size={14} className="text-blue-500" />
                  {profile.email || "No Email"}
                </span>

                <span className="flex items-center gap-1">
                  <Phone size={14} className="text-green-500" />
                  {profile.phone || "No Phone"}
                </span>

                <span className="flex items-center gap-1">
                  <MapPin size={14} className="text-red-500" />
                  {profile.location || "No Location"}
                </span>

              </div>
            </div>
          </div>

        </Card>

        {/* STATS */}
        <div className="grid md:grid-cols-4 gap-4 mb-6">

          {/* EXPERIENCE */}
          <Card className="p-4">
            <p className="text-xs text-gray-500">Experience</p>
            <div className="flex gap-2 mt-1">
              <Briefcase className="text-purple-600" />
              {getexperience()}
            </div>
          </Card>

          {/* CURRENT CTC */}
          <Card className="p-4">
            <p className="text-xs text-gray-500">Current CTC</p>
            <div className="flex gap-2 mt-1">
              <IndianRupee className="text-green-600" />
              ₹ {profile.currentSalary?.lakh || 0}.
              {profile.currentSalary?.thousand || 0} LPA
            </div>
          </Card>

          {/* EXPECTED CTC */}
          <Card className="p-4">
            <p className="text-xs text-gray-500">Expected CTC</p>
            <div className="flex gap-2 mt-1">
              <IndianRupee className="text-blue-600" />
              ₹ {profile.expectedSalary?.lakh || 0}.
              {profile.expectedSalary?.thousand || 0} LPA
            </div>
          </Card>

          {/* NOTICE */}
          <Card className="p-4">
            <p className="text-xs text-gray-500">Notice Period</p>
            <div className="flex gap-2 mt-1">
              <Clock className="text-orange-500" />
              {profile.noticePeriod || 0} Days
            </div>
          </Card>

        </div>

        {/* SKILLS */}
        <Card className="p-6 mb-6">
          <h3 className="font-semibold mb-3">Skills</h3>

          {profile.skills && profile.skills.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {profile.skills.map((skill, i) => (
                <Badge key={i} className="bg-black text-white">
                  {skill}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No skills added</p>
          )}
        </Card>

        {/* EXPERIENCE LIST */}
        <Card className="p-6 mb-6">
          <h3 className="font-semibold mb-4">Experience</h3>

          {profile.workExperience?.length ? (
            profile.workExperience.map((exp, i) => (
              <div key={i} className="mb-3">
                <p className="font-medium">{exp.role}</p>
                <p className="text-sm text-gray-500">{exp.company}</p>
              </div>
            ))
          ) : (
            <p className="text-gray-500">No experience added</p>
          )}
        </Card>

        {/* EDUCATION */}
        <Card className="p-6 mb-6">
          <h3 className="font-semibold mb-4">Education</h3>

          {profile.education?.length ? (
            profile.education.map((edu, i) => (
              <div key={i} className="mb-3">
                <p className="font-medium">{edu.degree}</p>
                <p className="text-sm text-gray-500">
                  {edu.course} • {edu.fieldOfStudy}
                </p>
                <p className="text-sm text-gray-500">{edu.institution}</p>
              </div>
            ))
          ) : (
            <p className="text-gray-500">No education added</p>
          )}
        </Card>

        {/* RESUME */}
        <Card className="p-6 mb-10">
          <h3 className="font-semibold mb-4">Resume</h3>

          {profile.resumeUrl ? (
            <a
              href={`${BACKEND_URL}${profile.resumeUrl}`}
              target="_blank"
              className="flex items-center gap-2 text-blue-600"
            >
              <Download size={16} />
              {profile.resumeFileName || "Download Resume"}
            </a>
          ) : (
            <p>No resume uploaded</p>
          )}

          <label className="mt-3 block cursor-pointer">
            <div className="bg-blue-600 text-white px-4 py-2 rounded inline-flex gap-2">
              <Upload size={16} />
              {uploading ? "Uploading..." : "Upload Resume"}
            </div>
            <Input type="file" className="hidden" onChange={handleResumeUpload} />
          </label>
        </Card>

      </div>

      {/* EDIT BUTTON */}
      <button
        onClick={() => navigate(`/edit-profile/${email}`)}
        className="fixed bottom-6 right-6 bg-blue-600 text-white px-4 py-3 rounded-full shadow-lg flex gap-2"
      >
        <Edit size={16} />
        Edit Profile
      </button>

      <Footer />
    </div>
  );
};

export default ViewProfile;